/**
 * Task 15 — remote adapter error surfaces.
 * Messages name operation + table only — never balances, amounts, merchants, or suffixes
 * (docs/50 privacy / financial integrity).
 */

export type RemoteErrorCode =
  | 'validation'
  | 'not_found'
  | 'forbidden'
  | 'conflict'
  | 'append_only_violation'
  | 'immutable_violation'
  | 'transport'
  | 'unknown';

export type RemoteOperation =
  'insert' | 'update' | 'soft_delete' | 'select' | 'list' | 'select_since';

export class RemoteRepositoryError extends Error {
  readonly code: RemoteErrorCode;
  readonly table: string;
  readonly operation: RemoteOperation;

  constructor(args: {
    code: RemoteErrorCode;
    table: string;
    operation: RemoteOperation;
    /** Short machine-safe detail — identifiers and codes only, never financial values. */
    detail?: string | undefined;
  }) {
    const detail = args.detail !== undefined && args.detail.length > 0 ? `: ${args.detail}` : '';
    super(`remote ${args.operation} on ${args.table} failed (${args.code})${detail}`);
    this.name = 'RemoteRepositoryError';
    this.code = args.code;
    this.table = args.table;
    this.operation = args.operation;
  }
}

/** Map PostgREST / supabase-js error shapes to a stable code. */
export function classifyTransportError(error: {
  message?: string;
  code?: string;
  status?: number;
}): RemoteErrorCode {
  const code = error.code ?? '';
  const status = error.status;
  const message = (error.message ?? '').toLowerCase();

  if (
    status === 401 ||
    status === 403 ||
    code === '42501' ||
    message.includes('row-level security')
  ) {
    return 'forbidden';
  }
  if (status === 404 || code === 'PGRST116') {
    return 'not_found';
  }
  if (status === 409 || code === '23505') {
    return 'conflict';
  }
  if (message.includes('fetch') || message.includes('network') || code === 'ECONNREFUSED') {
    return 'transport';
  }
  return 'unknown';
}
